import { useEffect, useRef, useState } from 'react';
import type { SyncStatus } from '@bhorosha/data';
import type { Membership, Session } from '@bhorosha/data';
import { t } from '@bhorosha/i18n';
import { initials } from '../ui/components';

export function AccountMenu({ session, membership, memberships, sync, onOpenFamily, onOpenAccount, onSwitchFamily, onNewFamily, onSignOut }: {
  session: Session;
  membership: Membership;
  memberships: Membership[];
  sync: { status: SyncStatus; pending: number };
  onOpenFamily: () => void;
  onOpenAccount: () => void;
  onSwitchFamily: (id: string) => void;
  onNewFamily: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const act = (fn: () => void) => () => { setOpen(false); fn(); };
  const others = memberships.filter(m => m.familyId !== membership.familyId);

  return (
    <div className="bh-acct no-print" ref={ref}>
      <button className="bh-acct-btn" onClick={() => setOpen(o => !o)} title={`${session.email} · ${membership.familyName}`} aria-haspopup="menu" aria-expanded={open}>
        <span className={`bh-sync-dot bh-sync-${sync.status}`} title={t(`sync.${sync.status}`)} />
        <span className="bh-acct-avatar">{initials(session.displayName || session.email)}</span>
        <span className="bh-acct-text">
          <span className="bh-acct-family">{membership.familyName}</span>
          <span className="bh-acct-role">{membership.role === 'viewer' ? t('menu.viewOnly') : t(`role.${membership.role}`)}</span>
        </span>
        <span className="caret">&#9660;</span>
      </button>
      {open && (
        <div className="bh-menu" role="menu">
          <div className="bh-menu-head">
            <div className="bh-strong">{session.displayName}</div>
            <div className="bh-small bh-muted">{session.email}</div>
            <div className={`bh-small bh-sync-text bh-sync-${sync.status}`}>{t(`sync.${sync.status}`)}{sync.status === 'saving' && sync.pending ? ` (${sync.pending})` : ''}</div>
          </div>
          <button role="menuitem" onClick={act(onOpenFamily)}>&#128106; {t('menu.family')}</button>
          {others.length > 0 && <div className="bh-menu-label">{t('menu.switchFamily')}</div>}
          {others.map(m => (
            <button role="menuitem" key={m.familyId} onClick={act(() => onSwitchFamily(m.familyId))}>&#8644; {m.familyName} <span className="bh-muted bh-small">· {t(`role.${m.role}`)}</span></button>
          ))}
          <button role="menuitem" onClick={act(onNewFamily)}>+ {t('menu.newFamily')}</button>
          <div className="bh-menu-sep" />
          <button role="menuitem" onClick={act(onOpenAccount)}>&#128100; {t('menu.account')}</button>
          <button role="menuitem" onClick={act(onSignOut)}>&#8617; {t('menu.signOut')}</button>
        </div>
      )}
    </div>
  );
}
